import ScheduledMeetingActions from "../ScheduledMeetingActions";
// src/components/job-details/JobDetailsActivitiesTab.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Button,
  Divider,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { EmailOutlined as EmailOutlinedIcon } from "../icons/FigmaControlIcons";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import { BoltOutlined as BoltOutlinedIcon } from "../icons/FigmaControlIcons";
import { DocumentText1, Trash } from "iconsax-react";
import FigmaIcon from "../icons/FigmaIcon";

import JobTabHeader from "./JobTabHeader";
import StageViewAccordionList from "./StageViewAccordionList";
import {
  activityToolbarRowSx,
  activityToolbarDropdownGroupSx,
  activityToolbarMenuItemSx,
  activityToolbarSelectSx,
  activityToolbarTextFieldSx,
} from "../activityToolbarStyles";
import { toJobActivityItem } from "../../utils/entityActivities";
import { readStoredUser } from "../../utils/userSession";

const PURPLE = "#9C4FFF";
const BORDER = "#E5E7EB";
const MUTED = "#94A3B8";
const ACTIVITY_HEADING_SX = {
  color: "#3451F4",
  fontSize: 16,
  fontWeight: 700,
  lineHeight: "24px",
};
const ACTIVITY_PARAGRAPH_SX = {
  fontSize: 14,
  fontWeight: 400,
};

/* -------------------- tiny type icons (left) -------------------- */
function TypeIcon({ type }) {
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        display: "grid",
        placeItems: "center",
        flex: "0 0 auto",
      }}
    >
      {type === "Email" ? (
        <EmailOutlinedIcon sx={{ width: 24, height: 24, color: "#3451F4" }} />
      ) : type === "Note" ? (
        <NoteAltOutlinedIcon sx={{ width: 24, height: 24, color: "#3451F4" }} />
      ) : (
        <BoltOutlinedIcon sx={{ width: 24, height: 24, color: "#3451F4" }} />
      )}
    </Box>
  );
}

function ActivityAccordionIcon({ type }) {
  if (type === "Email") {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" stroke="#3451F4" strokeWidth="1" />
        <path d="m4.5 7 7.5 5.5L19.5 7" stroke="#3451F4" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === "Note") {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 4.5h14v15H8.5L5 16.5v-12Z" stroke="#3451F4" strokeWidth="1" strokeLinejoin="round" />
        <path d="M8 9h8M8 12h6" stroke="#3451F4" strokeWidth="1" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m13 2-8 12h6l-1 8 9-13h-6l0-7Z" stroke="#3451F4" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* -------------------- card-like activity row (used in GRID) -------------------- */
function ActivityCard({ item }) {
  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: 2,
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 2, py: 1.6 }}>
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
          <TypeIcon type={item.type} />

          <Box sx={{ minWidth: 0, flex: 1 }}>
            {/* title row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Typography
                sx={{ ...ACTIVITY_HEADING_SX, fontFamily: "Be Vietnam Pro", minWidth: 0 }}
                noWrap
              >
                {item.title}
              </Typography>

              {item.badge ? (
                <Box
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: 999,
                    fontFamily: "Be Vietnam Pro",
                    fontSize: 10,
                    fontWeight: 900,
                    color: PURPLE,
                    bgcolor: "rgba(156,79,255,0.10)",
                    flex: "0 0 auto",
                  }}
                >
                  {item.badge}
                </Box>
              ) : null}

              <Box sx={{ flex: 1 }} />

              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  ...ACTIVITY_PARAGRAPH_SX,
                  color: MUTED,
                  whiteSpace: "nowrap",
                }}
              >
                {item.time}
              </Typography>
            </Box>

            {item.meta ? (
              <Typography
                sx={{
                  mt: 0.35,
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 10.5,
                  fontWeight: 800,
                  color: MUTED,
                  lineHeight: 1.25,
                }}
              >
                {item.meta}
              </Typography>
            ) : null}

            {item.body ? (
              <Typography
                sx={{
                  mt: 0.65,
                  fontFamily: "Be Vietnam Pro",
                  ...ACTIVITY_PARAGRAPH_SX,
                  color: "#475569",
                  lineHeight: 1.35,
                  whiteSpace: "pre-line",
                }}
              >
                {item.body}
              </Typography>
            ) : null}

            {item.attachment ? (
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<DocumentText1 size="16" color={PURPLE} />}
                  sx={{
                    textTransform: "none",
                    borderRadius: 999,
                    borderColor: "rgba(156,79,255,0.35)",
                    bgcolor: "rgba(156,79,255,0.06)",
                    color: PURPLE,
                    fontFamily: "Be Vietnam Pro",
                    fontWeight: 900,
                    fontSize: 11,
                    px: 1.3,
                    py: 0.6,
                    "&:hover": {
                      borderColor: "rgba(156,79,255,0.55)",
                      bgcolor: "rgba(156,79,255,0.08)",
                    },
                  }}
                >
                  {item.attachment}
                </Button>
              </Box>
            ) : null}

            {item.raw?.metadata?.calendar_event_id && <ScheduledMeetingActions record={{ ...item.raw.metadata, id: item.raw.id, actor_user_id: item.raw.actor_user_id }} />}
        {(!item.raw?.metadata?.calendar_event_id && (item.actions?.length || item.showTrash)) && (
              <Box
                sx={{
                  mt: 1.1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  flexWrap: "wrap",
                }}
              >
                {(item.actions || []).map((a) => (
                  <Typography
                    key={a}
                    sx={{
                      fontFamily: "Be Vietnam Pro",
                      fontSize: 10.5,
                      fontWeight: 900,
                      color: a.toLowerCase().includes("delete") ? MUTED : PURPLE,
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    {a}
                  </Typography>
                ))}

                {item.showTrash ? (
                  <IconButton size="small" sx={{ ml: "auto", p: 0.4 }}>
                    <Trash size="16" color={MUTED} />
                  </IconButton>
                ) : null}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/* -------------------- flat list row (used in LIST) -------------------- */
function ActivityListRow({ item, isLast }) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.6,
        borderBottom: isLast ? "none" : "1px solid #F1F5F9",
        display: "flex",
        gap: 1.25,
        alignItems: "flex-start",
      }}
    >
      <TypeIcon type={item.type} />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography
            sx={{ ...ACTIVITY_HEADING_SX, fontFamily: "Be Vietnam Pro", minWidth: 0 }}
            noWrap
          >
            {item.title}
          </Typography>

          {item.badge ? (
            <Box
              sx={{
                px: 1,
                py: 0.35,
                borderRadius: 999,
                fontFamily: "Be Vietnam Pro",
                fontSize: 10,
                fontWeight: 900,
                color: PURPLE,
                bgcolor: "rgba(156,79,255,0.10)",
                flex: "0 0 auto",
              }}
            >
              {item.badge}
            </Box>
          ) : null}

          <Box sx={{ flex: 1 }} />

          <Typography
            sx={{
              fontFamily: "Be Vietnam Pro",
              ...ACTIVITY_PARAGRAPH_SX,
              color: MUTED,
              whiteSpace: "nowrap",
            }}
          >
            {item.time}
          </Typography>
        </Box>

        {item.meta ? (
          <Typography
            sx={{
              mt: 0.35,
              fontFamily: "Be Vietnam Pro",
              fontSize: 10.5,
              fontWeight: 800,
              color: MUTED,
              lineHeight: 1.25,
            }}
          >
            {item.meta}
          </Typography>
        ) : null}

        {item.body ? (
          <Typography
            sx={{
              mt: 0.65,
              fontFamily: "Be Vietnam Pro",
              ...ACTIVITY_PARAGRAPH_SX,
              color: "#475569",
              lineHeight: 1.35,
              whiteSpace: "pre-line",
            }}
          >
            {item.body}
          </Typography>
        ) : null}

        {item.attachment ? (
          <Box sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              startIcon={<DocumentText1 size="16" color={PURPLE} />}
              sx={{
                textTransform: "none",
                borderRadius: 999,
                borderColor: "rgba(156,79,255,0.35)",
                bgcolor: "rgba(156,79,255,0.06)",
                color: PURPLE,
                fontFamily: "Be Vietnam Pro",
                fontWeight: 900,
                fontSize: 11,
                px: 1.3,
                py: 0.6,
                "&:hover": {
                  borderColor: "rgba(156,79,255,0.55)",
                  bgcolor: "rgba(156,79,255,0.08)",
                },
              }}
            >
              {item.attachment}
            </Button>
          </Box>
        ) : null}

        {item.raw?.metadata?.calendar_event_id && <ScheduledMeetingActions record={{ ...item.raw.metadata, id: item.raw.id, actor_user_id: item.raw.actor_user_id }} />}
        {(!item.raw?.metadata?.calendar_event_id && (item.actions?.length || item.showTrash)) && (
          <Box sx={{ mt: 1.1, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
            {(item.actions || []).map((a) => (
              <Typography
                key={a}
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 10.5,
                  fontWeight: 900,
                  color: a.toLowerCase().includes("delete") ? MUTED : PURPLE,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {a}
              </Typography>
            ))}

            {item.showTrash ? (
              <IconButton size="small" sx={{ ml: "auto", p: 0.4 }}>
                <Trash size="16" color={MUTED} />
              </IconButton>
            ) : null}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* -------------------- renderers for StageViewAccordionList -------------------- */
function getItems(arg1) {
  if (Array.isArray(arg1)) return arg1;
  return Array.isArray(arg1?.items) ? arg1.items : [];
}

function renderStageList(arg1) {
  return <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
    {getItems(arg1).map((item, index) => <Box key={item.id || index} sx={{ border: `1px solid ${BORDER}`, borderRadius: "8px", overflow: "hidden", bgcolor: "#fff" }}>
      <ActivityListRow item={item} isLast />
    </Box>)}
  </Box>;
}

function renderStageGrid(arg1) {
  const items = getItems(arg1);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
        gap: 1.25,
      }}
    >
      {items.map((it, idx) => (
        <ActivityCard key={it.id || idx} item={it} />
      ))}
    </Box>
  );
}

export default function JobDetailsActivitiesTab({ data, onEditHeader }) {
  const [query, setQuery] = useState("");
  const [type, setType] = useState("All Activities");
  const [sort, setSort] = useState("Newest");
  const currentRecruiterName = useMemo(() => {
    const storedUser = readStoredUser();
    return storedUser?.name || storedUser?.full_name || "Recruiter";
  }, []);

  // one pool of activities; we'll split into sections (accordions) like screenshot
  const activities = useMemo(
    () => (Array.isArray(data?.activities)
      ? data.activities
          .map((activity) => toJobActivityItem(activity))
          .filter(Boolean)
      : [
      {
        id: "a1",
        type: "Email",
        title: "Email from Lead: Posh",
        meta: "To: recruiter@company.com • Subject: Cc1 2000 interview confirmation",
        body: `Hi ${currentRecruiterName},\nPlease let me know if any adjustments are needed.`,
        attachment: "Olivia-Offer-Letter-2023-01.pdf",
        actions: ["Read More", "Delete"],
        time: "Feb 10, 2026 11:02am",
        badge: "New",
        showTrash: true,
        ts: new Date("2026-02-10T11:02:00").getTime(),
      },
      {
        id: "a2",
        type: "Action",
        title: "Action",
        meta: "Olivia Rye manually updated the availability for: Senior Dev Engineer (Onsite interview)",
        body:
          "Fri Feb 2, 3:00pm – 3:30pm (Pacific Time) (US & Canada)\n" +
          "Tue Feb 6, 1:00pm – 1:30pm (Pacific Time) (US & Canada)\n" +
          "Wed Feb 7, 4:00pm – 4:30pm (Pacific Time) (US & Canada)",
        actions: ["Edit", "Delete"],
        time: "Feb 7, 2026 1:56pm",
        showTrash: false,
        ts: new Date("2026-02-07T13:56:00").getTime(),
      },
      {
        id: "a3",
        type: "Note",
        title: "Note",
        meta: `Added by: ${currentRecruiterName}`,
        body: "@all Please see candidate's note for interview.",
        actions: ["Edit", "Delete"],
        time: "Feb 7, 2026 9:28am",
        showTrash: false,
        ts: new Date("2026-02-07T09:28:00").getTime(),
      },
      {
        id: "a4",
        type: "Note",
        title: "Note",
        meta: `Added by: ${currentRecruiterName}`,
        body: "@all The candidate completed a Course by Cybex in Security Engineering.",
        actions: ["Edit", "Delete"],
        time: "Feb 6, 2026 11:50am",
        showTrash: false,
        ts: new Date("2026-02-06T11:50:00").getTime(),
      },
      {
        id: "a5",
        type: "Action",
        title: "Action",
        meta: "Olivia Rye manually updated the availability for: Senior Dev Engineer (Onsite interview)",
        body:
          "Fri Feb 3, 3:00pm – 3:30pm (Pacific Time) (US & Canada)\n" +
          "Mon Feb 6, 1:00pm – 1:30pm (Pacific Time) (US & Canada)",
        actions: ["Edit", "Delete"],
        time: "Feb 3, 2026 1:56pm",
        showTrash: false,
        ts: new Date("2026-02-03T13:56:00").getTime(),
      },
      {
        id: "a6",
        type: "Email",
        title: "Email from Lead: Posh",
        meta: "To: recruiter@company.com • Subject: Cc1 2000 interview confirmation",
        body: `Hi ${currentRecruiterName},\nPlease let me know if any adjustments are needed.`,
        attachment: "Olivia-Offer-Letter-2023-01.pdf",
        actions: ["Read More", "Delete"],
        time: "Jan 28, 2026 11:02am",
        badge: "New",
        showTrash: true,
        ts: new Date("2026-01-28T11:02:00").getTime(),
      },
      {
        id: "a7",
        type: "Note",
        title: "Note",
        meta: `Added by: ${currentRecruiterName}`,
        body: "@all The candidate completed a Course by Cybex in Security Engineering.",
        actions: ["Edit", "Delete"],
        time: "Jan 27, 2026 12:24am",
        showTrash: false,
        ts: new Date("2026-01-27T00:24:00").getTime(),
      },
      {
        id: "a8",
        type: "Action",
        title: "Action",
        meta: `${currentRecruiterName} manually updated the availability for: Senior Dev Engineer (Onsite interview)`,
        body:
          "Fri Feb 14, 3:00pm – 3:30pm (Pacific Time) (US & Canada)\n" +
          "Mon Feb 17, 1:00pm – 1:30pm (Pacific Time) (US & Canada)",
        actions: ["Edit", "Delete"],
        time: "Jan 27, 2026 11:56pm",
        showTrash: false,
        ts: new Date("2026-01-27T23:56:00").getTime(),
      },
    ]),
    [currentRecruiterName, data?.activities]
  );

  // filter + sort
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    let list = activities.filter((a) => {
      const okType = type === "All Activities" ? true : a.type === type;
      const okQuery = !q ? true : `${a.title} ${a.meta || ""} ${a.body || ""}`.toLowerCase().includes(q);
      return okType && okQuery;
    });

    list = [...list].sort((a, b) => {
      const at = a.ts || 0;
      const bt = b.ts || 0;
      return sort === "Oldest" ? at - bt : bt - at;
    });

    return list;
  }, [activities, query, type, sort]);

  // ✅ split into sections (each section is its own accordion with grid/list buttons)
  const sections = useMemo(() => {
    const byType = {
      Email: [],
      Action: [],
      Note: [],
    };

    (filtered || []).forEach((a) => {
      if (byType[a.type]) byType[a.type].push(a);
    });

    const makeIcon = (typeLabel) => {
      return <ActivityAccordionIcon type={typeLabel} />;
    };

    // Order like screenshot feel (Email, Action, Note)
    return [
      { key: "Email", icon: makeIcon("Email"), items: byType.Email },
      { key: "Action", icon: makeIcon("Action"), items: byType.Action },
      { key: "Note", icon: makeIcon("Note"), items: byType.Note },
    ];
  }, [filtered]);

  // expand all by default (like screenshot)
  const defaultExpanded = useMemo(() => ({ Email: true, Action: true, Note: true }), []);

  return (
    <Box sx={{ backgroundColor: "#fff" }}>
      <JobTabHeader
        companyLogo={data?.companyLogo}
        companyName={data?.companyName}
        companyDomain={data?.companyDomain}
        profileData={data}
        onEdit={onEditHeader}
        sx={{ mb: 1.25 }}
      />

      {/* top filter row */}
      <Box
        sx={{
          ...activityToolbarRowSx,
          mb: 1.25,
        }}
      >
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search..."
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FigmaIcon name="search" color="#9C4FFF" />
              </InputAdornment>
            ),
          }}
          sx={activityToolbarTextFieldSx}
        />

        <Box sx={activityToolbarDropdownGroupSx}>
          <FormControl size="small" sx={{ width: { xs: "100%", md: 324 }, maxWidth: "100%" }}>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              sx={activityToolbarSelectSx}
            >
              <MenuItem value="All Activities" sx={activityToolbarMenuItemSx}>All Activities</MenuItem>
              <MenuItem value="Email" sx={activityToolbarMenuItemSx}>Email</MenuItem>
              <MenuItem value="Action" sx={activityToolbarMenuItemSx}>Action</MenuItem>
              <MenuItem value="Note" sx={activityToolbarMenuItemSx}>Note</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ width: { xs: "100%", md: 324 }, maxWidth: "100%" }}>
            <Select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              sx={activityToolbarSelectSx}
            >
              <MenuItem value="Newest" sx={activityToolbarMenuItemSx}>Sort by</MenuItem>
              <MenuItem value="Oldest" sx={activityToolbarMenuItemSx}>Oldest</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.25, borderColor: "#EEF2F7" }} />

      {/* ✅ Each SECTION is an accordion (with grid/list view buttons) */}
      <StageViewAccordionList pageSize={10} pageResetKey={JSON.stringify([data?.id, query, type, sort])}
        emptyText="No activities yet." stages={sections.filter(section => type === "All Activities" || section.key === type)}
        defaultExpanded={defaultExpanded} defaultView="list" renderList={renderStageList} renderGrid={renderStageGrid} />
    </Box>
  );
}
